"use client";

import React from "react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Calendar, User, ArrowLeft, ChevronRight } from "lucide-react";


declare global {
  interface Window {
    wvcClient?: any;
  }
}

function MainPostConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div data-section-id="224612" className="animate-pulse space-y-8">
        <div className="h-8 w-32 bg-muted rounded"></div>
        <div className="h-16 w-3/4 bg-muted rounded"></div>
        <div className="flex gap-4">
          <div className="h-8 w-32 bg-muted rounded"></div>
          <div className="h-8 w-32 bg-muted rounded"></div>
        </div>
        <div className="h-96 w-full bg-muted rounded-xl"></div>
        <div className="space-y-4">
          <div className="h-4 w-full bg-muted rounded"></div>
          <div className="h-4 w-full bg-muted rounded"></div>
          <div className="h-4 w-5/6 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12 bg-destructive/10 rounded-none border border-destructive/20">
        <p className="text-destructive font-medium font-default">Unable to load the story.</p>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="text-center py-12 bg-muted/30 rounded-none border border-border">
        <p className="text-muted-foreground font-default">No articles found.</p>
        <Link to="#" className="mt-4 inline-block">
          <Button variant="outline" className="rounded-none border-primary text-primary hover:bg-primary hover:text-primary-foreground">Back to Home</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];
  const formattedDate = post.date ? new Date(post.date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }) : "";

  return (
    <article className="max-w-4xl mx-auto">
      <div className="mb-8">
        <Link to="#" className="inline-flex items-center text-sm font-medium text-muted-foreground hover:text-primary transition-colors">
          <ArrowLeft className="mr-2 h-4 w-4" />
          BACK TO FEED
        </Link>
      </div>

      <header className="mb-10">
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-serif text-foreground leading-tight mb-6 uppercase tracking-tight" dangerouslySetInnerHTML={{ __html: post.title }} />
        
        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <Avatar className="h-10 w-10 border border-secondary">
              <AvatarImage src={post.author?.avatar?.['48'] || ""} alt={post.author?.name || "Chef"} />
              <AvatarFallback className="bg-secondary text-secondary-foreground">
                <User className="h-5 w-5" />
              </AvatarFallback>
            </Avatar>
            <span className="font-medium text-foreground font-default">{post.author?.name || "Sultani Grill Team"}</span>
          </div>
          <Separator orientation="vertical" className="h-6 hidden sm:block bg-border" />
          <div className="flex items-center gap-2 font-default">
            <Calendar className="h-4 w-4 text-primary" />
            <time dateTime={post.date}>{formattedDate}</time>
          </div>
        </div>
      </header>

      {post.featuredImage && (
        <figure className="mb-12 rounded-none overflow-hidden shadow-2xl border border-border/50">
          <AspectRatio ratio={16 / 9}>
            <img 
              src={post.featuredImage} 
              alt={post.title} 
              className="object-cover w-full h-full transition-transform duration-700 hover:scale-105"
            />
          </AspectRatio>
        </figure>
      )}

      <div 
        className="text-base md:text-lg leading-relaxed text-foreground/90 space-y-6 font-default
        [&>h2]:text-3xl [&>h2]:font-bold [&>h2]:font-serif [&>h2]:mt-12 [&>h2]:mb-6 [&>h2]:text-primary [&>h2]:uppercase
        [&>h3]:text-2xl [&>h3]:font-semibold [&>h3]:font-serif [&>h3]:mt-8 [&>h3]:mb-4 [&>h3]:text-foreground [&>h3]:uppercase
        [&>p]:mb-6 
        [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:mb-6 [&>ul>li]:mb-2
        [&>ol]:list-decimal [&>ol]:pl-6 [&>ol]:mb-6 [&>ol>li]:mb-2
        [&>blockquote]:border-l-4 [&>blockquote]:border-primary [&>blockquote]:pl-6 [&>blockquote]:py-2 [&>blockquote]:italic [&>blockquote]:text-muted-foreground [&>blockquote]:bg-secondary/10 [&>blockquote]:rounded-none
        [&>a]:text-primary [&>a]:underline [&>a]:underline-offset-4 hover:[&>a]:text-primary/80
        [&>img]:rounded-none [&>img]:shadow-xl [&>img]:my-8"
        dangerouslySetInnerHTML={{ __html: post.content || "" }} 
      />

      <Separator className="my-12 bg-border" />

      <section className="bg-card rounded-none p-6 md:p-8 flex flex-col sm:flex-row gap-6 items-start sm:items-center border border-border/50">
        <Avatar className="h-24 w-24 border-2 border-secondary shadow-md shrink-0">
          <AvatarImage src={post.author?.avatar?.['96'] || ""} alt={post.author?.name || "Chef"} />
          <AvatarFallback className="bg-secondary text-secondary-foreground text-2xl">
            <User className="h-10 w-10" />
          </AvatarFallback>
        </Avatar>
        <div>
          <h3 className="text-xl font-bold font-serif mb-2 text-foreground uppercase tracking-wide">About {post.author?.name || "the Chef"}</h3>
          <p className="text-muted-foreground leading-relaxed font-default">
            Crafting the perfect fusion of Punjabi heritage and American grill culture. Our culinary team is dedicated to serving bold, certified halal flavors in the heart of Irving, TX.
          </p>
        </div>
      </section>
    </article>
  );
}

function RelatedPostsConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading || error || !posts || posts.length === 0) {
    return null; 
  }

  return (
    <div className="mt-20 pt-12 border-t border-border">
      <div className="flex items-center justify-between mb-8">
        <h2 className="text-3xl font-bold font-serif text-foreground uppercase tracking-tight">More from the Kitchen</h2>
        <Link to="#" className="hidden sm:flex items-center text-sm font-medium text-primary hover:text-primary/80 uppercase tracking-widest">
          View All <ChevronRight className="ml-1 h-4 w-4" />
        </Link>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {posts.slice(0, 3).map((post, i) => (
          <div key={post.id} data-index={i}>
            <Card className="h-full flex flex-col overflow-hidden hover:shadow-3xl transition-all duration-300 border-border/50 bg-card group rounded-none">
              {post.featuredImage && (
                <div className="aspect-video w-full overflow-hidden">
                  <img 
                    src={post.featuredImage} 
                    alt={post.title} 
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" 
                  />
                </div>
              )}
              <CardHeader className="p-6 pb-4">
                <div className="text-xs text-primary mb-3 flex items-center gap-2 font-default uppercase tracking-widest">
                  <Calendar className="h-3 w-3" />
                  {post.date ? new Date(post.date).toLocaleDateString('en-US') : ""}
                </div>
                <CardTitle className="text-xl font-bold font-serif line-clamp-2 group-hover:text-primary transition-colors uppercase">
                  <Link to={post.link || "#"} dangerouslySetInnerHTML={{ __html: post.title }} />
                </CardTitle>
              </CardHeader>
              <CardContent className="px-6 pb-4 flex-grow">
                <div 
                  className="text-sm text-muted-foreground line-clamp-3 font-default" 
                  dangerouslySetInnerHTML={{ __html: post.excerpt || "" }} 
                />
              </CardContent>
              <CardFooter className="px-6 pb-6 pt-0 mt-auto">
                <Link to={post.link || "#"} className="inline-flex items-center text-sm font-bold text-primary hover:text-primary/80 transition-colors uppercase tracking-widest">
                  Read Story <ArrowLeft className="ml-2 h-4 w-4 rotate-180" />
                </Link>
              </CardFooter>
            </Card>
          </div>
        ))}
      </div>
      
      <div className="mt-8 text-center sm:hidden">
        <Link to="#">
          <Button variant="outline" className="w-full rounded-none border-primary text-primary uppercase font-serif">View All Stories</Button>
        </Link>
      </div>
    </div>
  );
}

export default function SinglePostTemplateSection() {
  
  const getQueryParams = () => {
    if (typeof window !== 'undefined' && window.wvcClient) {
      return window.wvcClient.getCurrentWPQueryParams();
    }
    return { post_type: 'post' }; 
  };

  const currentWPQueryParams = getQueryParams();
  const mainQuery = new WP_Query(currentWPQueryParams);
  
  
  const relatedQuery = new WP_Query({ post_type: 'post', per_page: 3 });

  return (
    <section id="single-post-template-section" className="bg-background text-foreground py-16 md:py-24 lg:py-32">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        <PostsProvider wp_query={mainQuery}>
          <MainPostConsumer />
        </PostsProvider>

        <PostsProvider wp_query={{ post_type: "post", per_page: 3 }}>
          <RelatedPostsConsumer />
        </PostsProvider>
      </div>
    </section>
  );
}